
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Wedding, Locale, Theme, Plan } from '../types';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { normalizeWeddingFromSupabase } from '../lib/weddings/normalizeWeddingFromSupabase';
import { SectionKey, ALL_USERS_PRO_PROMO } from '../constants';
import { useAuth } from './AuthContext';

interface WeddingContextType {
  wedding: Wedding | null;
  weddings: Wedding[];
  activeWedding: Wedding | null;
  locale: Locale;
  theme: Theme;
  isLoading: boolean;
  setLocale: (l: Locale) => void;
  setTheme: (t: Theme) => void;
  refreshWedding: () => Promise<void>;
  refreshWeddings: () => Promise<void>;
  /** Update one wedding in the list (e.g. after saving in Settings) so UI shows persisted data. */
  updateWeddingInList: (weddingId: string, updates: Partial<Wedding>) => void;
  switchWedding: (id: string) => void;
  hasFeature: (section: SectionKey) => boolean;
}

const WeddingContext = createContext<WeddingContextType | undefined>(undefined);

// Hardcoded Seed ID for the "No Login" phase if no weddings exist
const DEFAULT_WEDDING_ID = '00000000-0000-0000-0000-000000000000';

export const WeddingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, accessToken } = useAuth();
  
  const [weddings, setWeddings] = useState<Wedding[]>([]);
  const [activeWeddingId, setActiveWeddingId] = useState<string | null>(() => localStorage.getItem('active_wedding_id') || DEFAULT_WEDDING_ID);
  const [activeWedding, setActiveWedding] = useState<Wedding | null>(null);
  
  const [locale, setLocaleState] = useState<Locale>(() => (localStorage.getItem('lang') as Locale) || 'cs');
  const [theme, setThemeState] = useState<Theme>(() => (localStorage.getItem('theme') as Theme) || 'system');
  const [isLoading, setIsLoading] = useState(true);

  const refreshWeddings = async () => {
    const supabase = getAuthenticatedSupabase();
    const { data } = await supabase.from('weddings').select('*');
    if (data) {
      setWeddings(data.map((row) => normalizeWeddingFromSupabase(row as Record<string, unknown>)));
      if (!activeWeddingId && data.length > 0) {
        setActiveWeddingId(data[0].id);
      }
    }
    setIsLoading(false);
  };

  // Načítat svatby pouze když je uživatel přihlášen
  // Reaguje na změnu accessToken (např. po přihlášení)
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      refreshWeddings();
    } else {
      // Když není přihlášen, vyčistit svatby
      setWeddings([]);
      setActiveWedding(null);
      setIsLoading(false);
    }
  }, [isAuthenticated, accessToken]);

  useEffect(() => {
    if (weddings.length > 0 && activeWeddingId) {
       const found = weddings.find(w => w.id === activeWeddingId);
       setActiveWedding(found || weddings[0]);
    } else if (weddings.length > 0) {
       setActiveWedding(weddings[0]);
    }
  }, [weddings, activeWeddingId]);

  useEffect(() => {
    localStorage.setItem('lang', locale);
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const switchWedding = (id: string) => {
    setActiveWeddingId(id);
    localStorage.setItem('active_wedding_id', id);
  };

  const updateWeddingInList = (weddingId: string, updates: Partial<Wedding>) => {
    setWeddings((prev) =>
      prev.map((w) => {
        if (w.id !== weddingId) return w;
        const merged = { ...(w as unknown as Record<string, unknown>), ...(updates as Record<string, unknown>) };
        return normalizeWeddingFromSupabase(merged);
      }),
    );
  };

  const setLocale = (l: Locale) => setLocaleState(l);
  const setTheme = (t: Theme) => setThemeState(t);

  const hasFeature = (section: SectionKey): boolean => {
    if (!activeWedding) return false;
    if (section.startsWith('divider')) return false;
    // Akce: všichni mají Pro – plný přístup
    if (ALL_USERS_PRO_PROMO) return true;
    // Feature gating: FREE nemá budget, templates, seating
    if (activeWedding.plan === Plan.FREE) {
       if (['budget', 'templates', 'seating'].includes(section)) return false;
    }
    return true;
  };

  return (
    <WeddingContext.Provider value={{ 
      wedding: activeWedding, 
      activeWedding,
      weddings, 
      locale, 
      theme, 
      isLoading, 
      setLocale, 
      setTheme, 
      refreshWedding: refreshWeddings, 
      refreshWeddings,
      updateWeddingInList,
      switchWedding,
      hasFeature 
    }}>
      {children}
    </WeddingContext.Provider>
  );
};

export const useWedding = () => {
  const context = useContext(WeddingContext);
  if (!context) throw new Error('useWedding must be used within WeddingProvider');
  return context;
};
